import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import {
  Bell, Check, X, ShieldAlert, Sparkles, AlertTriangle, Info, Crown, MegaphoneIcon,
  BookOpen, Wallet, GraduationCap, Home, Wrench,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Legacy "kind" fallback (info / success / warning / security / premium / system).
const KIND_META = {
  info: { color: "text-slate-500", bg: "bg-slate-50", Icon: Info },
  success: { color: "text-emerald-600", bg: "bg-emerald-50", Icon: Sparkles },
  warning: { color: "text-amber-600", bg: "bg-amber-50", Icon: AlertTriangle },
  security: { color: "text-rose-600", bg: "bg-rose-50", Icon: ShieldAlert },
  premium: { color: "text-indigo-600", bg: "bg-indigo-50", Icon: Crown },
  system: { color: "text-slate-600", bg: "bg-slate-100", Icon: MegaphoneIcon },
  broadcast: { color: "text-indigo-600", bg: "bg-indigo-50", Icon: MegaphoneIcon },
  digest: { color: "text-fuchsia-600", bg: "bg-fuchsia-50", Icon: MegaphoneIcon },
};

// Broadcast categories → icon + accent color
const CATEGORY_META = {
  news:          { color: "text-indigo-600", bg: "bg-indigo-50", Icon: MegaphoneIcon },
  blog:          { color: "text-fuchsia-600", bg: "bg-fuchsia-50", Icon: BookOpen },
  caf_dates:     { color: "text-emerald-600", bg: "bg-emerald-50", Icon: Wallet },
  crous_dates:   { color: "text-blue-600", bg: "bg-blue-50", Icon: GraduationCap },
  housing:       { color: "text-pink-600", bg: "bg-pink-50", Icon: Home },
  scam_alerts:   { color: "text-rose-600", bg: "bg-rose-50", Icon: ShieldAlert },
  app_updates:   { color: "text-amber-600", bg: "bg-amber-50", Icon: Sparkles },
  maintenance:   { color: "text-slate-600", bg: "bg-slate-100", Icon: Wrench },
  premium_promo: { color: "text-indigo-600", bg: "bg-indigo-50", Icon: Crown },
};

const PRIORITY_BORDER = {
  urgent: "border-l-4 border-rose-500",
  high: "border-l-4 border-amber-500",
  normal: "",
  low: "",
};

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 604800) return `il y a ${Math.floor(diff / 86400)} j`;
    return d.toLocaleDateString("fr-FR");
  } catch {
    return "";
  }
}

function pickMeta(n) {
  if (n.category && CATEGORY_META[n.category]) return CATEGORY_META[n.category];
  return KIND_META[n.kind] || KIND_META.info;
}

export default function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const trackedOpens = useRef(new Set());

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=50");
      setItems(data || []);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, 60_000);
    return () => clearInterval(t);
  }, [fetchUnread]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  // Fire tracking "opened" events once the panel shows a broadcast for the 1st time.
  useEffect(() => {
    if (!open || items.length === 0) return;
    const toTrack = items.filter(
      (n) => n.campaign_id && !n.opened_at && !trackedOpens.current.has(n.id)
    );
    toTrack.forEach((n) => {
      trackedOpens.current.add(n.id);
      api.post(`/notifications/${n.id}/opened`).catch(() => {});
    });
  }, [open, items]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-read", { ids: null });
      setItems(items.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  const clearOne = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setItems(items.filter((n) => n.id !== id));
      await fetchUnread();
    } catch { /* silent */ }
  };

  const handleClick = async (n) => {
    if (!n.read) {
      try {
        await api.post("/notifications/mark-read", { ids: [n.id] });
        setItems(items.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setUnread(Math.max(0, unread - 1));
      } catch { /* silent */ }
    }
    // Track click for broadcast campaigns (before nav so it always fires)
    if (n.campaign_id && !n.clicked_at) {
      api.post(`/notifications/${n.id}/clicked`).catch(() => {});
    }
    if (n.link) {
      setOpen(false);
      // External links open in a new tab, internal links use react-router
      if (/^https?:\/\//i.test(n.link)) {
        window.open(n.link, "_blank", "noopener,noreferrer");
      } else {
        navigate(n.link);
      }
    }
  };

  return (
    <div className="relative" data-testid="notif-center">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 rounded-full bg-white border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 flex items-center justify-center transition-colors"
        aria-label="Notifications"
        data-testid="notif-bell-btn"
      >
        <Bell size={16} className="text-slate-600" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center leading-none ring-2 ring-white"
            data-testid="notif-unread-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40 md:hidden"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            className="absolute right-0 mt-2 w-[92vw] max-w-[380px] rounded-2xl border border-slate-200 bg-white shadow-2xl z-50 overflow-hidden"
            data-testid="notif-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
              <p className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                <Bell size={14} className="text-indigo-500" />
                Notifications
              </p>
              <div className="flex items-center gap-2">
                {unread > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-[11px] text-indigo-600 hover:text-indigo-700 font-medium"
                    data-testid="notif-mark-all-read-btn"
                  >
                    <Check size={12} className="inline -mt-px mr-1" />
                    Tout marquer lu
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="Fermer"
                  data-testid="notif-close-btn"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="max-h-[440px] overflow-y-auto">
              {loading && (
                <p className="p-6 text-center text-xs text-slate-400">Chargement…</p>
              )}
              {!loading && items.length === 0 && (
                <div className="p-8 text-center">
                  <Bell size={28} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500 font-medium">Aucune notification</p>
                  <p className="text-xs text-slate-400 mt-1">Tu es à jour ! 👌</p>
                </div>
              )}              {items.map((n) => {
                const meta = pickMeta(n);
                const IconC = meta.Icon;
                const border = PRIORITY_BORDER[n.priority] || "";
                return (
                  <div
                    key={n.id}
                    className={`group px-4 py-3 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-slate-50 transition-colors ${!n.read ? "bg-indigo-50/40" : ""} ${border}`}
                    onClick={() => handleClick(n)}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full ${meta.bg} flex items-center justify-center shrink-0`}>
                        <IconC size={14} className={meta.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!n.read ? "font-semibold text-slate-900" : "text-slate-700"} truncate`}>
                          {n.title}
                        </p>
                        {n.body && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2 whitespace-pre-line">{n.body}</p>
                        )}
                        {n.cover_image_url && (
                          <div className="mt-2 -mx-1 rounded-lg overflow-hidden bg-slate-100" data-testid={`notif-cover-${n.id}`}>
                            <img
                              src={n.cover_image_url}
                              alt=""
                              loading="lazy"
                              className="w-full h-24 object-cover"
                              onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                            />
                          </div>
                        )}
                        <p className="text-[11px] text-slate-400 mt-1">
                          {timeAgo(n.created_at)}
                          {n.priority === "urgent" && (
                            <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[9px] font-bold uppercase tracking-wide">
                              Urgent
                            </span>
                          )}
                        </p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); clearOne(n.id); }}
                        className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500 transition-opacity"
                        aria-label="Supprimer"
                        data-testid={`notif-delete-${n.id}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-slate-100 px-4 py-2.5 bg-slate-50 flex items-center justify-center gap-1 text-[11px]">
              <button
                onClick={() => { setOpen(false); navigate("/notifications/history"); }}
                className="text-indigo-600 hover:text-indigo-700 font-medium"
                data-testid="notif-history-link"
              >
                Voir l'historique complet des digests →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

